import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Bar, CartesianGrid, ComposedChart, Legend, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { cashflowAnalysis, type CashflowInsight, type InsightTone } from "@torre/expenses";
import { useTheme } from "../../../context/ThemeContext";
import { apiErrorMessage, fetchCashflow, fetchLosses, type CashflowResponse, type LossesResponse } from "../services/expensesService";
import { ErrorBox, Kpi, Notice, Page, Spinner } from "../components/ui";
import { CATEGORY_COLORS, categoryLabel, currentMonth, money, monthLabel, shiftMonth } from "../lib/format";
import { ui } from "../theme";
import PayoutCard from "../components/PayoutCard";

// Flujo: la portada que junta y explica las tres piezas.
//
//   A  Ingresos   lo que entró            → Flujo → Ingresos
//   B  Gastos     lo que pagó la empresa  → Flujo → Gastos
//   C  Pérdidas   la parte de B que no dejó nada → Flujo → Pérdidas
//
// Flujo neto = A − B. C no se resta otra vez: parte el gasto en productivo y
// perdido. Los indicadores y los hallazgos salen de `cashflowAnalysis`
// (@torre/expenses); aquí solo se les pone texto y forma.

const RANGES = [6, 12, 24];
const COLORS = { income: "#2a78d6", productive: "#eb6834", loss: "#dc2626", cumulative: "#059669", company: "#eb6834", personal: "#1baf7a" };
const TONE: Record<InsightTone, { icon: string; color: string }> = {
  good: { icon: "ri-checkbox-circle-line", color: "text-emerald-500" },
  warn: { icon: "ri-alert-line", color: "text-amber-500" },
  bad: { icon: "ri-close-circle-line", color: "text-red-500" },
  info: { icon: "ri-information-line", color: "text-sky-500" },
};

const pct = (ratio: number | null | undefined) => (ratio == null ? "—" : `${Math.round(ratio * 1000) / 10}%`);

export default function CashflowSection() {
  const { t, i18n } = useTranslation("common");
  const { isDarkMode } = useTheme();
  const c = ui(isDarkMode);
  const navigate = useNavigate();
  const lang = i18n.language;
  const [months, setMonths] = useState(12);
  const [data, setData] = useState<CashflowResponse | null>(null);
  // `null` = no se pudieron leer: el análisis lo dice, no lo toma como cero.
  const [losses, setLosses] = useState<LossesResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    const to = currentMonth();
    const from = shiftMonth(to, -(months - 1));
    setData(null);
    setLosses(null);
    Promise.all([fetchCashflow({ from, to }), fetchLosses({ from, to }).catch(() => null)])
      .then(([flow, loss]) => {
        if (!alive) return;
        setData(flow);
        setLosses(loss);
        setError(null);
      })
      .catch((err) => alive && setError(apiErrorMessage(err) || "load"));
    return () => {
      alive = false;
    };
  }, [months]);

  const analysis = useMemo(() => (data ? cashflowAnalysis({ flow: data, losses, currentPeriod: currentMonth() }) : null), [data, losses]);

  const m = (value: number | null | undefined) => money(value, "MXN", lang);
  /** Texto de un hallazgo: los montos y porcentajes se formatean aquí. */
  const insightText = (i: CashflowInsight) => {
    const p = i.params;
    const params: Record<string, string | number> = { ...p };
    for (const key of ["net", "spend", "losses", "productive"] as const) if (typeof p[key] === "number") params[key] = m(p[key] as number);
    for (const key of ["margin", "share", "change"] as const) if (typeof p[key] === "number") params[key] = pct(Math.abs(p[key] as number));
    if (typeof p.coverage === "number") params.coverage = pct(p.coverage);
    if (typeof p.category === "string") params.category = categoryLabel(p.category, lang);
    if (i.code === "incomeTrend" || i.code === "spendTrend") return t(`expenses.flow.insight.${i.code}${(p.change as number) > 0 ? "Up" : "Down"}`, params);
    return t(`expenses.flow.insight.${i.code}`, params);
  };

  const chart = (analysis?.months || []).map((row) => ({ label: monthLabel(row.period, lang), income: row.income, productive: row.productive, loss: row.loss, cumulative: row.cumulative }));
  const axis = isDarkMode ? "#9ca3af" : "#6b7280";
  const catMax = Math.max(1, ...(data?.categories || []).map((x) => x.company + x.personal));
  // Puente del ingreso al flujo neto: cada renglón contra el mayor de los dos lados.
  const scale = analysis ? Math.max(analysis.income, analysis.spend, 1) : 1;
  const bridge = analysis
    ? [
        { key: "income", sign: "", value: analysis.income, color: COLORS.income },
        { key: "productive", sign: "−", value: analysis.productiveSpend, color: COLORS.productive },
        { key: "loss", sign: "−", value: analysis.losses, color: COLORS.loss },
        { key: "net", sign: "=", value: analysis.net, color: analysis.net < 0 ? COLORS.loss : COLORS.cumulative },
      ]
    : [];

  return (
    <Page
      title={t("expenses.menu.cashflow")}
      subtitle={t("expenses.flow.subtitle")}
      actions={
        <select aria-label={t("expenses.summary.range")} className={`${c.inputBase} w-auto`} value={months} onChange={(e) => setMonths(Number(e.target.value))}>
          {RANGES.map((r) => (
            <option key={r} value={r}>
              {t("expenses.summary.lastMonths", { count: r })}
            </option>
          ))}
        </select>
      }
    >
      <ErrorBox message={error === "load" ? t("expenses.errors.load") : error} />
      {!data && !error && <Spinner />}
      {data && analysis && (
        <>
          {/* A, B y C: cada pieza abre su detalle. */}
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4" data-testid="flow-pieces">
            {(
              [
                { letter: "A", id: "income", label: t("expenses.menu.income"), value: m(analysis.income), hint: t("expenses.cashflow.incomeHint", { count: data.incomeCount }), tone: "" },
                { letter: "B", id: "expenses", label: t("expenses.menu.expenses"), value: m(analysis.spend), hint: t("expenses.flow.spendHint"), tone: "" },
                {
                  letter: "C",
                  id: "losses",
                  label: t("expenses.menu.losses"),
                  value: analysis.losses == null ? "—" : m(analysis.losses),
                  hint: analysis.losses == null ? t("expenses.flow.lossesUnknown") : t("expenses.flow.lossesHint", { share: pct(analysis.lossShare) }),
                  tone: (analysis.losses || 0) > 0 ? "text-red-500" : "",
                },
              ] as const
            ).map((piece) => (
              <button key={piece.id} type="button" onClick={() => navigate(`/expenses/${piece.id}`)} className={`${c.card} text-left transition-shadow hover:shadow-md`} data-testid={`flow-piece-${piece.id}`}>
                <p className={`flex items-center gap-2 text-xs ${c.muted}`}>
                  <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-teal-600 text-[11px] font-bold text-white">{piece.letter}</span>
                  {piece.label}
                  <i className="ri-arrow-right-up-line ml-auto" />
                </p>
                <p className={`mt-1 text-2xl font-bold tabular-nums ${piece.tone || c.text}`}>{piece.value}</p>
                <p className={`mt-0.5 text-xs ${c.muted}`}>{piece.hint}</p>
              </button>
            ))}
            <Kpi label={t("expenses.flow.net")} value={m(analysis.net)} tone={analysis.net < 0 ? "bad" : "good"} hint={t("expenses.flow.netHint", { margin: pct(analysis.margin) })} />
          </div>

          {data.withoutRate > 0 && <Notice tone="warn">{t("expenses.list.withoutRate", { count: data.withoutRate })}</Notice>}
          {data.incomeCount === 0 && <Notice tone="info">{t("expenses.cashflow.noIncome")}</Notice>}

          {/* Lectura del periodo: qué dicen A, B y C juntos. */}
          <div className={c.card} data-testid="flow-analysis">
            <h3 className={`text-sm font-semibold ${c.text}`}>{t("expenses.flow.readingTitle")}</h3>
            <p className={`mb-3 text-xs ${c.muted}`}>{t("expenses.flow.readingHint")}</p>
            {analysis.insights.length === 0 ? (
              <p className={`text-sm ${c.muted}`}>{t("expenses.flow.empty")}</p>
            ) : (
              <ul className="space-y-2">
                {analysis.insights.map((i) => (
                  <li key={i.code} className={`flex items-start gap-2 text-sm ${c.text}`} data-testid={`insight-${i.code}`}>
                    <i className={`${TONE[i.tone].icon} ${TONE[i.tone].color} mt-0.5 text-base`} />
                    <span>{insightText(i)}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            {/* Del ingreso al flujo neto. */}
            <div className={c.card} data-testid="flow-bridge">
              <h3 className={`mb-3 text-sm font-semibold ${c.text}`}>{t("expenses.flow.bridgeTitle")}</h3>
              <ul className="space-y-3">
                {bridge.map((row) => (
                  <li key={row.key}>
                    <div className={`flex items-baseline justify-between text-sm ${c.text}`}>
                      <span>
                        <span className={`mr-2 inline-block w-3 text-center font-semibold ${c.muted}`}>{row.sign}</span>
                        {t(`expenses.flow.bridge.${row.key}`)}
                      </span>
                      <span className="font-semibold tabular-nums">
                        {row.value == null ? "—" : m(row.value)}
                        {row.value != null && analysis.income > 0 && <span className={`ml-2 text-xs font-normal ${c.muted}`}>{pct(row.value / analysis.income)}</span>}
                      </span>
                    </div>
                    <div className={`mt-1 h-2 w-full overflow-hidden rounded-full ${isDarkMode ? "bg-white/10" : "bg-gray-100"}`}>
                      <div className="h-full rounded-full" style={{ width: `${Math.min(100, (Math.abs(row.value || 0) / scale) * 100)}%`, background: row.color }} />
                    </div>
                  </li>
                ))}
              </ul>
              <p className={`mt-3 text-xs ${c.muted}`}>{t("expenses.flow.bridgeNote")}</p>
            </div>

            {/* Indicadores. */}
            <div className={c.card} data-testid="flow-indicators">
              <h3 className={`mb-2 text-sm font-semibold ${c.text}`}>{t("expenses.flow.indicatorsTitle")}</h3>
              <table className="w-full text-sm">
                <tbody>
                  {(
                    [
                      ["margin", pct(analysis.margin)],
                      ["coverage", pct(analysis.coverage)],
                      ["lossShare", pct(analysis.lossShare)],
                      ["avgIncome", m(analysis.avgIncome)],
                      ["avgSpend", m(analysis.avgSpend)],
                      ["positiveMonths", `${analysis.positiveMonths} / ${analysis.activeMonths}`],
                      ["best", analysis.best ? `${monthLabel(analysis.best.period, lang)} · ${m(analysis.best.net)}` : "—"],
                      ["worst", analysis.worst ? `${monthLabel(analysis.worst.period, lang)} · ${m(analysis.worst.net)}` : "—"],
                      ["topPayer", analysis.topPayer ? `${analysis.topPayer.payer} · ${pct(analysis.topPayer.share)}` : "—"],
                      ["topCategory", analysis.topCategory ? `${categoryLabel(analysis.topCategory.category, lang)} · ${pct(analysis.topCategory.share)}` : "—"],
                    ] as const
                  ).map(([key, value]) => (
                    <tr key={key} className={`border-t ${c.border} ${c.text}`}>
                      <td className="py-1.5">
                        {t(`expenses.flow.indicator.${key}`)}
                        <span className={`block text-[11px] ${c.muted}`}>{t(`expenses.flow.indicatorHint.${key}`)}</span>
                      </td>
                      <td className="py-1.5 text-right font-semibold tabular-nums">{value}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className={c.card}>
            <h3 className={`text-sm font-semibold ${c.text}`}>{t("expenses.flow.chartTitle")}</h3>
            <p className={`mb-3 text-xs ${c.muted}`}>{t("expenses.flow.chartHint")}</p>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={chart} barGap={2}>
                  <CartesianGrid strokeDasharray="3 3" stroke={isDarkMode ? "#333" : "#e5e7eb"} vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 11, fill: axis }} />
                  <YAxis tick={{ fontSize: 11, fill: axis }} tickFormatter={(v) => `$${Math.round(Number(v) / 1000)}k`} width={52} />
                  <Tooltip formatter={(v: number, name: string) => [m(v), t(`expenses.flow.series.${name}`)]} contentStyle={{ fontSize: 12 }} />
                  <Legend formatter={(v: string) => t(`expenses.flow.series.${v}`)} wrapperStyle={{ fontSize: 11 }} />
                  <Bar dataKey="income" fill={COLORS.income} radius={[4, 4, 0, 0]} isAnimationActive={false} />
                  <Bar dataKey="productive" stackId="spend" fill={COLORS.productive} isAnimationActive={false} />
                  <Bar dataKey="loss" stackId="spend" fill={COLORS.loss} radius={[4, 4, 0, 0]} isAnimationActive={false} />
                  <Line dataKey="cumulative" type="monotone" stroke={COLORS.cumulative} strokeWidth={2} dot={false} isAnimationActive={false} />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Detalle del gasto de la empresa: de dónde sale y a dónde va. */}
          <h2 className={`pt-2 text-base font-semibold ${c.text}`}>{t("expenses.flow.detailTitle")}</h2>
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <Kpi label={t("expenses.cashflow.personalSpend")} value={m(data.totals.personalSpend)} hint={t("expenses.cashflow.personalHint")} />
            <Kpi label={t("expenses.cashflow.reimbursableYes")} value={m(data.reimbursable.yes)} hint={t("expenses.cashflow.charges", { count: data.reimbursable.yesCount })} />
            <Kpi label={t("expenses.cashflow.reimbursableNo")} value={m(data.reimbursable.no)} hint={t("expenses.cashflow.charges", { count: data.reimbursable.noCount })} />
            <Kpi
              label={t("expenses.cashflow.reimbursablePending")}
              value={m(data.reimbursable.pending)}
              hint={t("expenses.cashflow.charges", { count: data.reimbursable.pendingCount })}
              tone={data.reimbursable.pendingCount > 0 ? "bad" : undefined}
            />
          </div>
          {(data.totals.refunds > 0 || data.totals.personalIncome > 0) && (
            <p className={`text-xs ${c.muted}`}>{t("expenses.cashflow.refundsNote", { refunds: m(data.totals.refunds), personalIncome: m(data.totals.personalIncome) })}</p>
          )}

          <PayoutCard />

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
            <div className={c.card}>
              <h3 className={`mb-2 text-sm font-semibold ${c.text}`}>{t("expenses.cashflow.whereMoneyGoes")}</h3>
              <div className={`mb-2 flex gap-4 text-xs ${c.muted}`}>
                <span className="inline-flex items-center gap-1.5">
                  <span className="h-2 w-2 rounded-sm" style={{ background: COLORS.company }} />
                  {t("expenses.cashflow.series.company")}
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <span className="h-2 w-2 rounded-sm" style={{ background: COLORS.personal }} />
                  {t("expenses.cashflow.series.personal")}
                </span>
              </div>
              <ul className="space-y-2">
                {data.categories.map((row) => {
                  const total = row.company + row.personal;
                  return (
                    <li key={row.category}>
                      <div className={`flex justify-between text-xs ${c.text}`}>
                        <span className="flex items-center gap-1.5">
                          <span className="h-2 w-2 rounded-full" style={{ background: CATEGORY_COLORS[row.category] }} />
                          {categoryLabel(row.category, lang)}
                        </span>
                        <span className="tabular-nums">{m(total)}</span>
                      </div>
                      <div className="mt-1 flex h-1.5 gap-0.5" style={{ width: `${(total / catMax) * 100}%` }}>
                        {row.company > 0 && <div className="h-full" style={{ flex: row.company, background: COLORS.company }} />}
                        {row.personal > 0 && <div className="h-full rounded-r" style={{ flex: row.personal, background: COLORS.personal }} />}
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>

            <div className={c.card}>
              <h3 className={`mb-2 text-sm font-semibold ${c.text}`}>{t("expenses.cashflow.attribution")}</h3>
              <table className="w-full text-sm">
                <tbody>
                  {Object.entries(data.attribution)
                    .sort((a, b) => b[1] - a[1])
                    .map(([reason, value]) => (
                      <tr key={reason} className={`border-t ${c.border} ${c.text}`}>
                        <td className="py-1.5">{t(`expenses.cashflow.reason.${reason}`, { defaultValue: reason })}</td>
                        <td className="py-1.5 text-right font-semibold tabular-nums">{m(value)}</td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>

            <div className={c.card}>
              <h3 className={`mb-2 text-sm font-semibold ${c.text}`}>{t("expenses.cashflow.topVendors")}</h3>
              <table className="w-full text-sm">
                <tbody>
                  {data.topVendors.map((v) => (
                    <tr key={v.vendor} className={`border-t ${c.border} ${c.text}`}>
                      <td className="max-w-[10rem] truncate py-1.5" title={v.vendor}>
                        {v.vendor}
                      </td>
                      <td className={`py-1.5 text-right text-xs ${c.muted}`}>{v.count}</td>
                      <td className="py-1.5 text-right font-semibold tabular-nums">{m(v.total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </Page>
  );
}
